import crypto from 'crypto-js';
import {
    LoginCredentials,
    SignupData,
    LoginResponse,
    AuthUser,
    BackendSignupRequest,
    BackendLoginRequest,
    BackendAuthResponse,
    PasswordResetResponse
} from '../types';
import { AUTH_STORAGE_KEYS } from '../constants';
import { ENV } from '../../../config/env';

// Constants - matching your working repo
const ALLOWED_ROLES = ['candidate', 'company'];

class AuthApiService {
    private readonly baseURL: string = ENV.API_BASE_URL;
    private readonly timeout: number = 15000;

    constructor() {
        // baseURL initialized from ENV
    }

    // =====================================
    // 🔐 PASSWORD ENCRYPTION (Exact Copy)
    // =====================================

    /**
     * Hash and encrypt password - EXACT copy of your working repo logic
     */
    /**
     * Hash and encrypt password - Shortened version for Supabase 72-char limit
     */
    private async hashAndEncryptPassword(password: string): Promise<string> {
        try {
            // Step 1: Hash the password using SHA-256
            const hash = crypto.SHA256(password).toString(crypto.enc.Hex);

            // Step 2: Take only first 32 characters of hash to make it shorter
            const shortHash = hash.substring(0, 32);

            // Step 3: Create a MUCH shorter compliant string
            const compliant = `Aa1!${shortHash}Z@`;

            // Step 4: Base64 encode (this should now be under 72 chars)
            const encrypted = btoa(compliant);

            // Step 5: Ensure it's under 72 characters
            if (encrypted.length > 72) {
                return encrypted.substring(0, 72);
            }

            return encrypted;
        } catch (error: any) {
            console.error('🚨 Password encryption failed:', error);
            throw new Error('Password encryption failed: ' + error.message);
        }
    }


    // =====================================
    // 🌐 HTTP REQUEST HANDLER
    // =====================================

    /**
     * Generic HTTP Request Handler - similar to your axios setup
     */
    private async makeRequest<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const url = `${this.baseURL}${endpoint}`;
        // Making request to endpoint

        // Create proper headers object
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...((options.headers as Record<string, string>) || {})
        };

        const config: RequestInit = {
            ...options,
            headers,
            signal: AbortSignal.timeout(this.timeout) // Modern timeout approach
        };

        try {
            const response = await fetch(url, config);

            // Handle different response types
            let responseData: any;
            try {
                responseData = await response.json();
            } catch (parseError) {
                console.warn('Failed to parse JSON response:', parseError);
                responseData = {
                    error: response.ok ? 'Invalid response format' : `HTTP ${response.status}`
                };
            }

            // Handle HTTP errors
            if (!response.ok) {
                const errorMessage = responseData?.status?.msg ||
                    responseData?.detail ||
                    responseData?.message ||
                    responseData?.error ||
                    `HTTP error! status: ${response.status}`;
                throw new Error(errorMessage);
            }

            // Unwrap the backend response format: { payload: {...}, status: {...} }
            // The Signup-Service returns data wrapped in a "payload" field
            if (responseData.payload !== undefined) {
                return responseData.payload;
            }

            return responseData;

        } catch (error: any) {
            console.error('🚨 API Request failed:', error);

            // Handle specific error types like your working repo
            if (error.name === 'TimeoutError' || error.code === 'ECONNABORTED') {
                throw new Error('Request timed out - backend not responding');
            }

            if (error.message?.includes('fetch') || error.message?.includes('Network')) {
                throw new Error('Backend server is currently unavailable. Please try again later or contact support.');
            }

            // Re-throw API errors
            throw error;
        }
    }

    /**
     * Get authorization headers for authenticated requests
     */
    private getAuthHeaders(): Record<string, string> {
        const token = this.getStoredToken();
        return token ? { 'Authorization': `Bearer ${token}` } : {};
    }

    // =====================================
    // 🔑 AUTHENTICATION METHODS
    // =====================================

    /**
     * User Registration - Following your working repo pattern EXACTLY
     */
    async register(userData: SignupData): Promise<LoginResponse> {
        try {

            // Input Validation (matching your working repo)
            const { firstName, lastName, email, password, professionalRole, userType, companyName } = userData;

            // Determine role from userType or default to 'candidate'
            const role = userType || 'candidate';

            // For candidates, professionalRole is required. For company, it's not needed
            if (role === 'candidate' && !professionalRole) {
                throw new Error('Professional role is required for candidate registration');
            }

            // Validation differs by role:
            // - Candidates: require firstName, lastName, email, password
            // - Companies: require companyName, email, password (no firstName/lastName)
            if (role === 'company') {
                if (!companyName || !email || !password) {
                    throw new Error('Company name, email and password are required');
                }
            } else {
                if (!firstName || !lastName || !email || !password) {
                    throw new Error('All fields are required');
                }
            }

            if (password.length < 6) {
                throw new Error('Password must be at least 6 characters long');
            }

            if (!ALLOWED_ROLES.includes(role)) {
                throw new Error(`Invalid role selected. Only ${ALLOWED_ROLES.join(' and ')} registrations are allowed. Recruiters must use invitation links from companies.`);
            }

            // ✅ Send plain password - Supabase needs plain password to create user
            // Supabase handles password hashing internally
            // DO NOT encrypt/hash the password on the frontend

            // Transform data to match backend schema (camelCase for Go services)
            const backendPayload: BackendSignupRequest = {
                firstName: firstName,
                lastName: lastName,
                email: email,
                password: password,  // ✅ Send plain password, not encrypted
                ...(userData.companyName && role === 'company' ? { companyName: userData.companyName } : {})
            };


            // Determine endpoint based on userType (separate endpoints per integration guide)
            const endpoint = role === 'candidate'
                ? '/auth/v1/signup/candidate'
                : '/auth/v1/signup/company';

            // Make API call (following integration guide v1 endpoints)
            await this.makeRequest<{ message: string; user_role: string; user_id: string }>(
                endpoint,
                {
                    method: 'POST',
                    body: JSON.stringify(backendPayload),
                }
            );


            // Auto-login after successful signup
            return await this.login({
                email: email,
                password: password, // Use original password for login
            });

        } catch (error: any) {
            console.error('🚨 Registration failed:', error);

            // Handle specific backend error cases (matching your working repo)
            if (error.message?.includes('already exists') || error.message?.includes('already registered')) {
                throw new Error('EMAIL_ALREADY_EXISTS');
            }

            if (error.message?.includes('invite-only')) {
                throw new Error('RECRUITER_INVITE_ONLY');
            }

            // Return the original error message
            throw new Error(error.message || 'Registration failed');
        }
    }

    /**
     * User Login - Following your working repo pattern
     */
    async login(credentials: LoginCredentials): Promise<LoginResponse> {
        try {

            // Validate input
            if (!credentials.email || !credentials.password) {
                throw new Error("Email and password are required");
            }

            // ✅ Send plain password - backend doesn't need encryption for login
            // The backend validates password directly with Supabase
            // Only signup needs encryption because it creates the password in Supabase

            // Prepare request data according to backend contract
            const requestData: BackendLoginRequest = {
                email: credentials.email,
                password: credentials.password  // ✅ Send plain password
            };


            // Make API call (using v1 endpoint per integration guide)
            const response = await this.makeRequest<BackendAuthResponse>(
                '/auth/v1/login',
                {
                    method: 'POST',
                    body: JSON.stringify(requestData),
                }
            );

            // Transform backend response to frontend format (using camelCase from Go services)
            const loginResponse: LoginResponse = {
                user: {
                    id: response.user.id,              // MongoDB ObjectID - primary user identifier
                    supabaseId: response.user.supabaseId, // Supabase UUID (for auth provider compatibility)
                    email: response.user.email,
                    firstName: response.user.firstName,
                    lastName: response.user.lastName,
                    userType: response.user.role as 'candidate' | 'company' | 'recruiter' | 'hush_admin',
                    isEmailVerified: response.user.isVerified,
                    profile_completed: response.user.profileCompleted,
                    createdAt: new Date().toISOString(),
                },
                token: response.accessToken,
                refreshToken: response.refreshToken,
                expiresIn: 3600,
            };

            // Store authentication data
            await this.setAuthData(loginResponse);

            // Fetch and store company data for company/recruiter users
            if (loginResponse.user.userType === 'company' || loginResponse.user.userType === 'recruiter') {
                await this.fetchAndStoreCompanyData(loginResponse.token);
            }

            return loginResponse;

        } catch (error: any) {
            console.error('🚨 Login failed:', error);

            // Handle specific backend error cases
            const errorMessage = error.message || 'Login failed';

            if (errorMessage.includes('401') ||
                errorMessage.toLowerCase().includes('invalid') ||
                errorMessage.toLowerCase().includes('unauthorized')) {
                throw new Error('INVALID_CREDENTIALS');
            }

            throw new Error(errorMessage);
        }
    }

    /**
     * Get Current User
     */
    async getCurrentUser(): Promise<AuthUser> {
        try {
            const token = this.getStoredToken();
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await this.makeRequest<BackendAuthResponse['user']>(
                '/auth/v1/me',
                {
                    headers: this.getAuthHeaders(),
                }
            );

            // Transform backend response to frontend format (using camelCase from Go services)
            return {
                id: response.id,                    // MongoDB ObjectID - primary user identifier
                supabaseId: response.supabaseId,    // Supabase UUID (for auth provider compatibility)
                email: response.email,
                firstName: response.firstName,
                lastName: response.lastName,
                userType: response.role as 'candidate' | 'company' | 'recruiter' | 'hush_admin',
                isEmailVerified: response.isVerified,
                profile_completed: response.profileCompleted,
                createdAt: new Date().toISOString(),
            };

        } catch (error: any) {
            throw new Error(error.message || 'Failed to get current user');
        }
    }

    /**
     * User Logout
     */
    async logout(): Promise<void> {
        try {
            await this.makeRequest<{ message: string }>(
                '/auth/v1/logout',
                {
                    method: 'POST',
                    headers: this.getAuthHeaders(),
                }
            );
        } catch (error) {
            console.warn('Logout API call failed:', error);
        } finally {
            await this.clearAuthData();
        }
    }

    /**
     * Refresh Authentication Token
     */
    async refreshToken(): Promise<string> {
        try {
            const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
            if (!refreshToken) {
                throw new Error('No refresh token available');
            }

            const response = await this.makeRequest<{ accessToken: string; refreshToken: string }>(
                '/auth/v1/refresh',
                {
                    method: 'POST',
                    body: JSON.stringify({ refreshToken: refreshToken }),  // camelCase
                }
            );

            // Update stored tokens (using camelCase from Go services)
            localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, response.accessToken);
            if (response.refreshToken) {
                localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, response.refreshToken);
            }

            return response.accessToken;

        } catch (error: any) {
            await this.clearAuthData();
            throw new Error('Session expired. Please log in again.');
        }
    }





    /**
     * Forgot Password - Send reset email
     */
    async forgotPassword(email: string): Promise<PasswordResetResponse> {
        try {

            // Input validation
            if (!email || !email.trim()) {
                throw new Error('Email address is required');
            }

            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                throw new Error('Please enter a valid email address');
            }

            const response = await this.makeRequest<PasswordResetResponse>(
                '/auth/v1/password/forgot',
                {
                    method: 'POST',
                    body: JSON.stringify({ email: email.trim().toLowerCase() }),
                }
            );

            return response;

        } catch (error: any) {
            console.error(' Forgot password failed:', error);
            // Return generic message for security (don't reveal if email exists)
            return {
                message: "If an account with that email exists, we've sent a password reset link."
            };
        }
    }

    /**
     * Reset Password - Update password with new one
     */
    async resetPassword(token: string, newPassword: string): Promise<PasswordResetResponse> {
        try {

            // Input validation
            if (!token || !token.trim()) {
                throw new Error('Reset token is required');
            }

            if (!newPassword) {
                throw new Error('New password is required');
            }

            // Password strength validation
            if (newPassword.length < 8) {
                throw new Error('Password must be at least 8 characters long');
            }

            if (newPassword.length > 128) {
                throw new Error('Password is too long (maximum 128 characters)');
            }

            // Password complexity check
            const hasUpperCase = /[A-Z]/.test(newPassword);
            const hasLowerCase = /[a-z]/.test(newPassword);
            const hasNumbers = /\d/.test(newPassword);
            const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword);

            if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
                throw new Error('Password must contain at least one uppercase letter, one lowercase letter, and one number');
            }

            // Clean the token
            const cleanToken = token.trim();

            // ✅ Send plain password - backend handles validation

            const response = await this.makeRequest<PasswordResetResponse>(
                '/auth/v1/password/reset',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        token: cleanToken,
                        password: newPassword  // ✅ Fixed: was 'new_password', now 'password' to match backend
                    }),
                }
            );

            return response;

        } catch (error: any) {
            console.error('🚨 Password reset failed:', error);

            // Handle specific error cases with user-friendly messages
            const errorMessage = error.message || 'Failed to reset password';

            if (errorMessage.includes('expired') || errorMessage.includes('invalid token')) {
                throw new Error('This reset link has expired or is invalid. Please request a new password reset.');
            }

            if (errorMessage.includes('already used')) {
                throw new Error('This reset link has already been used. Please request a new password reset.');
            }

            if (errorMessage.includes('user not found')) {
                throw new Error('Account not found. Please request a new password reset.');
            }

            if (errorMessage.includes('timeout')) {
                throw new Error('Request timed out. Please check your connection and try again.');
            }

            if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
                throw new Error('Connection error. Please check your internet connection and try again.');
            }

            // Password validation errors (pass through as-is)
            if (errorMessage.includes('Password must') || errorMessage.includes('password')) {
                throw new Error(errorMessage);
            }

            // Generic fallback error
            throw new Error('Failed to reset password. Please try again or request a new reset link.');
        }
    }

    /**
     * Setup Password - Set password for the first time
     */
    async setupPassword(token: string, password: string): Promise<{ message: string }> {
        try {

            // Input validation
            if (!token) throw new Error('Token is required');
            if (!password) throw new Error('Password is required');
            if (password.length < 8) throw new Error('Password must be at least 8 characters long');

            const response = await this.makeRequest<{ message: string }>(
                '/setup/v1/password',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        token: token,
                        password: password
                    }),
                }
            );

            return response;
        } catch (error: any) {
            console.error('🚨 Setup password failed:', error);
            throw new Error(error.message || 'Failed to set password');
        }
    }

    /**
     * Verify Reset Token - Check if reset token is valid
     * Uses GET /setup/v1/token/:token/validate endpoint
     */
    async verifyResetToken(token: string): Promise<{ valid: boolean; email?: string; error?: string }> {
        try {
            if (!token || !token.trim()) {
                console.warn('❌ No token provided for verification');
                return { valid: false, error: 'Reset token is required' };
            }

            const cleanToken = token.trim();

            // Use the correct backend endpoint: GET /setup/v1/token/:token/validate
            const response = await this.makeRequest<{ valid: boolean; email?: string; firstName?: string; lastName?: string; message?: string }>(
                `/setup/v1/token/${encodeURIComponent(cleanToken)}/validate`,
                {
                    method: 'GET',
                }
            );
            return response;

        } catch (error: any) {
            if (error.message?.includes('404') || error.message?.toLowerCase().includes('not found')) {
                return { valid: false, error: 'Reset link not found' };
            }

            if (error.message?.includes('401') || error.message?.toLowerCase().includes('unauthorized')) {
                return { valid: false, error: 'Reset link has expired' };
            }

            if (error.message?.includes('timeout')) {
                return { valid: false, error: 'Request timed out. Please try again.' };
            }

            return { valid: false, error: 'Unable to verify reset link. Please request a new one.' };
        }
    }

    // =====================================
    // 💾 STORAGE MANAGEMENT
    // =====================================

    /**
     * Store authentication data securely
     */
    private async setAuthData(data: LoginResponse): Promise<void> {
        try {
            localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, data.token);
            localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
            localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(data.user));
        } catch (error) {
            console.error('Failed to store authentication data:', error);
            throw new Error('Failed to save login session');
        }
    }

    /**
     * Clear all authentication data
     */
    private async clearAuthData(): Promise<void> {
        try {
            localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN);
            localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
            localStorage.removeItem(AUTH_STORAGE_KEYS.USER);
            // Clear company-related data
            localStorage.removeItem('companyData');
            localStorage.removeItem('recruiterProfile');
        } catch (error) {
            console.error('Failed to clear authentication data:', error);
        }
    }

    /**
     * Get stored authentication token
     */
    private getStoredToken(): string | null {
        return localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
    }

    /**
     * Get stored user data
     */
    getStoredUser(): AuthUser | null {
        try {
            const userStr = localStorage.getItem(AUTH_STORAGE_KEYS.USER);
            return userStr ? JSON.parse(userStr) : null;
        } catch (error) {
            console.error('Failed to parse stored user data:', error);
            return null;
        }
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        const token = this.getStoredToken();
        const user = this.getStoredUser();
        return !!(token && user);
    }
    
    /**
     * Fetch and store company data for company/recruiter users
     * Called after login to populate companyData in localStorage
     */
    async fetchAndStoreCompanyData(accessToken: string): Promise<void> {
        try {
            const companyServiceUrl = import.meta.env.VITE_COMPANY_SERVICE_URL || 'http://127.0.0.1:8001';
            const url = `${companyServiceUrl}/companies/v1/my-companies`;


            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                console.warn('⚠️ Failed to fetch company data:', response.status);
                return;
            }

            const result = await response.json();

            // Handle Go service response format with payload wrapper
            const companies = result.payload || result.data || result || [];

            if (Array.isArray(companies) && companies.length > 0) {
                const primaryCompany = companies[0];
                localStorage.setItem('companyData', JSON.stringify(primaryCompany));
            } else {
            }
        } catch (error) {
            console.error('❌ Failed to fetch company data:', error);
            // Don't throw - this is a non-critical operation
        }
    }

    /**
     * Update profile completion status
     */
    async updateProfileCompleted(userId: string): Promise<void> {
        try {

            const response = await this.makeRequest<{ message: string }>(
                `/auth/v1/candidate/${userId}/profile/complete`,
                {
                    method: 'PATCH',
                    headers: this.getAuthHeaders(),
                }
            );


            // Update stored user data
            const user = this.getStoredUser();

            if (user) {
                user.profile_completed = true;
                localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user));
            } else {
                console.warn('⚠️  No user found in localStorage to update');
            }

            return;
        } catch (error: any) {
            console.error('🚨 Failed to update profile completion:', error);
            console.error('🚨 Error details:', {
                message: error.message,
                stack: error.stack
            });
            throw new Error(error.message || 'Failed to update profile completion status');
        }
    }
}

// Export singleton instance
export const authApiService = new AuthApiService();
